import React, {Fragment, useState, useEffect} from "react"
import {useSelector, useDispatch} from "react-redux"
import styled from "styled-components"
import {useForm, Controller, useFieldArray} from "react-hook-form"
import moment from "moment"
import {useAuth0} from "@auth0/auth0-react"
import {Container, Grid, Row, Col} from "@uprise/grid"
import {extended, semantic} from "@uprise/colors"
import {TextInputHorizontal} from "@uprise/form"
import {H4, H6, H5} from "@uprise/headings"
import {P} from "@uprise/text"
import {Button} from "@uprise/button"

import DropDown from "components/shared/DropDown"

import COUNTRY_CODES from "constants/countrycodes"

const FormElementContainer = styled.div`
	width: 100%;
	display: flex;
	justify-content: space-between;
	align-items: center;
	border-bottom: ${props => (props.error ? "1px solid #ff9999" : "1px solid #edeafa")};
`

const FormTextInput = styled(TextInputHorizontal)``

const FormElementLabel = styled(H6)`
	margin: 16px 0 8px 0;

	&:after {
		content: "${props => (props.isRequired ? "*" : "")}";
	}
`

const FormInputContainer = styled.div`
	flex-basis: 60%;
	position: relative;
	align-self: center;
`

const ButtonContainer = styled.div`
	display: flex;
`

const PrimaryButton = styled(Button)`
	width: 100%;
	min-width: 100px;
	height: 50px;
	margin: 10px 6px 0;
	outline: none;
`

const SecondaryButton = styled(Button)`
	width: 100%;
	min-width: 100px;
	height: 50px;
	margin: 10px 6px 0;
	border-color: transparent;
	background-color: #f6f4fc;
	outline: none;
`

const MessageStyles = styled.label`
	font-size: 12px;
	color: ${props => {
		if (props.focused) {
			return `${extended.blue.one}`
		} else if (props.validation) {
			return `${semantic.error}`
		} else {
			return `${extended.purple.five}`
		}
	}};
	display: block;
	margin-top: 8px;
	transition: font-size 0.2s;
`
export const PhoneInputWrapper = styled.div`
	display: flex;
	flex-basis: 60%;
	position: relative;
	align-self: center;
	justify-content: flex-start;
`

const EditUserDetails = ({data, onComplete, onCancel}) => {
	const {getAccessTokenSilently} = useAuth0()

	const {register, control, handleSubmit, watch, reset, trigger, setError, errors, setValue} = useForm({
		// defaultValues: {}; you can populate the fields by this attribute
		mode: "onSubmit",
		reValidateMode: "onChange"
	})
	return (
		<Container>
			<form
				onSubmit={handleSubmit(formData => {
					onComplete(formData)
				})}>
				<Row>
					<Col className='col-12 col-lg-12 col-md-12 col-sm-12'>
						<FormElementContainer
							error={errors.email?.type === "required" || errors.email?.type === "maxLength"}>
							<FormElementLabel isRequired={true}>User Email</FormElementLabel>
							<FormInputContainer>
								<Controller
									name={"email"}
									control={control}
									rules={{
										required: {
											value: true,
											message: "Email is Required"
										},
										maxLength: {
											value: 50,
											message: "User Email can not exceed 50 characters"
										}
									}}
									defaultValue={data.email}
									as={<FormTextInput id={"email"} type='email' placeholder='User Email' />}
								/>
							</FormInputContainer>
						</FormElementContainer>

						{errors["email"]?.message && (
							<MessageStyles htmlFor={1} focused={false} validation={errors["email"]}>
								{errors["email"]?.message}
							</MessageStyles>
						)}

						<FormElementContainer
							error={errors.name?.type === "required" || errors.name?.type === "maxLength"}>
							<FormElementLabel isRequired={true}>User Name</FormElementLabel>
							<FormInputContainer>
								<Controller
									name={"name"}
									control={control}
									rules={{
										required: {
											value: true,
											message: "User Name is Required"
										},
										maxLength: {
											value: 50,
											message: "User name can not exceed 50 characters"
										},
										minLength: {
											value: 2,
											message: "User name should be at least 2 characters long"
										}
									}}
									defaultValue={data.name}
									as={<FormTextInput id={"name"} type='text' placeholder='User Name' />}
								/>
							</FormInputContainer>
						</FormElementContainer>

						{errors["name"]?.message && (
							<MessageStyles htmlFor={1} focused={false} validation={errors["name"]}>
								{errors["name"]?.message}
							</MessageStyles>
						)}

						<FormElementContainer error={errors && errors.phoneNumber?.type == "required"}>
							<FormElementLabel isRequired={true}>Phone Number</FormElementLabel>
							<div style={{flexBasis: "60%", marginTop: "5px"}}>
								<PhoneInputWrapper>
									<Controller
										name={"countryCode"}
										control={control}
										defaultValue={{
											label: data.countryCode,
											value: data.countryCode
										}}
										render={props => (
											<DropDown
												variant='primary'
												className='m-l-10'
												title={"Country code"}
												items={COUNTRY_CODES}
												label='Select Code'
												onChange={value => {
													props.onChange(value)
												}}
												initialSelectedItem={{
													label: data.countryCode,
													value: data.countryCode
												}}
												rules={{
													required: {
														value: true,
														message: "Country code is required"
													}
												}}
												menuStyle={{position: "absolute", zIndex: 20000000}}
												containerStyle={{maringRight: "10px", width: "100px"}}
												customListItemRender={item => (
													<div style={{display: "flex", justifyContent: "center"}}>
														<p>{item.name}</p>
														<p>{item.label}</p>
													</div>
												)}
											/>
										)}
									/>

									<Controller
										name={"phoneNumber"}
										control={control}
										rules={{required: true}}
										defaultValue={data.phone}
										rules={{
											required: {
												value: true,
												message: "Phone Number is required"
											},
											pattern: {
												value: /\d/g,
												message: "Phone number should be valid"
											},
											maxLength: {
												value: 15,
												message: "Phone Number can be a maximum of 15 characters"
											}
										}}
										render={props => (
											<FormTextInput
												type='text'
												data-testid='phoneNumber'
												value={props.value}
												onChange={value => props.onChange(value)}
												placeholder='Phone Number'
												style={{marginTop: "35px", marginLeft: "10px"}}
											/>
										)}
									/>
								</PhoneInputWrapper>
							</div>
						</FormElementContainer>
						{errors["phoneNumber"]?.message && (
							<MessageStyles htmlFor={1} focused={false} validation={errors["phoneNumber"]}>
								{errors["phoneNumber"]?.message}
							</MessageStyles>
						)}
					</Col>
				</Row>
				<Row className='flex-row-reverse'>
					<ButtonContainer>
						<PrimaryButton type='submit' variant={"primary"} title={"Update"} />
					</ButtonContainer>
					<ButtonContainer>
						<SecondaryButton
							onClick={e => {
								e.preventDefault()
								onCancel()
							}}
							variant={"secondary"}
							title={"Cancel"}
						/>
					</ButtonContainer>
				</Row>
			</form>
		</Container>
	)
}

export default EditUserDetails
